import axios from "axios";
import { ChevronLeft, ChevronRight, X } from "feather-icons-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";

const DISMISSED_KEY = "dismissed-app-banners";

function bannerVersion(banner) {
  return `${banner.id}:${banner.updatedAt || "original"}`;
}

function readDismissed() {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

export function AppBanner() {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    axios.get("/api/banners", { params: { active: true } })
      .then((response) => {
        if (mounted) setBanners(response.data.data || []);
      })
      .catch((error) => {
        // Banners are supplementary and should never prevent the app from rendering.
        console.error("Error fetching app banners:", error);
      });

    return () => { mounted = false; };
  }, []);

  const visibleBanners = useMemo(
    () => banners.filter((banner) => !dismissed.includes(bannerVersion(banner))),
    [banners, dismissed]
  );

  useLayoutEffect(() => {
    const root = document.documentElement;
    const element = bannerRef.current;

    if (!element) {
      root.style.setProperty("--app-banner-offset", "0px");
      return undefined;
    }

    const updateOffset = () => {
      root.style.setProperty("--app-banner-offset", `${element.offsetHeight}px`);
    };

    updateOffset();
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(updateOffset);
    observer.observe(element);

    return () => observer.disconnect();
  }, [currentIndex, visibleBanners.length]);

  useEffect(() => () => {
    document.documentElement.style.removeProperty("--app-banner-offset");
  }, []);

  useEffect(() => {
    if (currentIndex >= visibleBanners.length) setCurrentIndex(0);
  }, [currentIndex, visibleBanners.length]);

  useEffect(() => {
    if (visibleBanners.length < 2 || paused) return undefined;

    const timer = window.setInterval(() => {
      setCurrentIndex((index) => (index + 1) % visibleBanners.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [paused, visibleBanners.length]);

  if (visibleBanners.length === 0) return null;

  const current = visibleBanners[currentIndex] || visibleBanners[0];
  const hasMultiple = visibleBanners.length > 1;

  const move = (direction) => {
    setCurrentIndex((index) => (index + direction + visibleBanners.length) % visibleBanners.length);
  };

  const dismiss = () => {
    const next = [...new Set([...dismissed, bannerVersion(current)])];
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    setDismissed(next);
  };

  return (
    <section
      ref={bannerRef}
      className={styles.banner}
      style={{ backgroundColor: current.backgroundColor, color: current.textColor }}
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      {hasMultiple && (
        <button className={`${styles.control} ${styles.previous}`} onClick={() => move(-1)} aria-label="Previous banner">
          <ChevronLeft size={22} />
        </button>
      )}

      <div className={styles.content}>
        <span>{current.message}</span>
      </div>

      {hasMultiple && (
        <button className={`${styles.control} ${styles.next}`} onClick={() => move(1)} aria-label="Next banner">
          <ChevronRight size={22} />
        </button>
      )}

      {current.canClose && (
        <button className={styles.close} onClick={dismiss} aria-label="Close banner">
          <X size={19} />
        </button>
      )}
    </section>
  );
}
